import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { UserService } from '../../../services/user.service';

import { Company, Person, User, UserDetails } from '../../../models/user';
import { ReciboCliente } from 'src/app/models/api-response';
import { UserIdentity } from 'src/app/models/api-response/user';
import { ApiService } from 'src/app/services/api/api.service';
import { NGXLogger } from 'ngx-logger';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { AuthService } from 'src/app/services/auth.service';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { HttpErrorHandlerService } from '@shared/services/http-error-handler.service';
import { applyValidationErrorsToForm, clearServerErrorsFromForm } from '@shared/utils/form-error.utils';
import { InstitutionalServiceContext } from 'src/app/components/client/institution-history/traffic-history.models';

@Component({
  selector: 'app-cli-perfil',
  templateUrl: './perfil.component.html',
  styleUrls: ['./perfil.component.scss'],
})
export class PerfilComponent implements OnInit, OnDestroy {
  activeTab = 'profile';

  editProfile = false;
  editProfileIcon = 'icon-edit';

  editContact = false;
  editContactIcon = 'icon-edit';

  editOtherInfo = false;
  editOtherInfoIcon = 'icon-edit';

  /**unsubscribe before leave this component*/
  private ngUnsubscribe = new Subject();

  identity: UserIdentity;

  usuario_id: number; // id del usuario actual
  data_saved_successfully: boolean; // indica si se guardaron correctamente los datos a la BD
  registration_successful_message?: string = undefined;
  registration_error_message?: string = undefined;

  user: User;
  person: Person;
  company: Company;

  form: FormGroup;
  passwordForm: FormGroup;
  isDelay: boolean;
  isPasswordSubmitting = false;
  /** Tras cambio correcto: espera UX antes de navegar a logout. */
  isPasswordChangeLogoutPending = false;
  private passwordChangeLogoutTimer?: ReturnType<typeof setTimeout>;
  private static readonly passwordChangeLogoutDelayMs = 2000;
  /** Visibilidad de contraseña (solo UI). */
  showCurrentPassword = false;
  showNewPassword = false;
  showNewPasswordConfirmation = false;
  isSchool: boolean = false;
  isUgel: boolean = false;
  /** Último servicio INSTITUCIONAL contratado (rol 6). Null = card con Estado ACTIVO. */
  institutionalService: InstitutionalServiceContext | null = null;

  /** Mientras se obtiene el usuario desde la API (skeleton en vista). */
  isLoadingProfile = true;

  /** Filas repetidas en plantilla skeleton (evita arrays inline en *ngFor). */
  readonly skeletonDataRows = [1, 2, 3];
  readonly skeletonServiceRows = [1, 2, 3, 4];
  readonly skeletonContactRows = [1, 2, 3, 4];
  readonly skeletonPasswordRows = [1, 2, 3];

  constructor(
    private _user: UserService,
    private formBuilder: FormBuilder,
    private _api: ApiService,
    private _log: NGXLogger,
    private route: ActivatedRoute,
    private location: Location,
    private _authService: AuthService,
    private readonly userAuth: UserAuthService,
    private readonly httpErrorHandler: HttpErrorHandlerService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
  ) {
    this.form = this.formBuilder.group({
      cellphone1: ['', Validators.required],
      cellphone2: [''],
      phone: [''],
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            '^([a-zA-Z0-9\\.\\-_]+\\@[a-zA-Z0-9\\-_]+\\.([a-zA-Z0-9\\-_]+\\.)?[a-zA-Z]+(\\.[a-zA-Z]+)?)?$',
          ),
        ],
      ],
    });

    this.passwordForm = this.formBuilder.group({
      currentPassword: ['', [Validators.required, Validators.minLength(6)]],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      newPasswordConfirmation: ['', [Validators.required, Validators.minLength(6), this.confirmMatchesNewPassword]],
    });

    this.passwordForm
      .get('newPassword')
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.passwordForm.get('newPasswordConfirmation')?.updateValueAndValidity({ emitEvent: false });
      });

    this.identity = this._user.getIdentity();

    this.isSchool = this._authService.isSchool();
    this.isUgel = this._authService.isUgel();
  }

  get emailInvalid() {
    return this.form.get('email')?.touched && this.form.get('email')?.errors;
  }

  get cellphone1Invalid() {
    return this.form.get('cellphone1')?.touched && this.form.get('cellphone1')?.errors;
  }

  private confirmMatchesNewPassword(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) {
      return null;
    }

    const newPassword = parent.get('newPassword')?.value as string | undefined;
    const confirm = control.value as string | undefined;

    if (confirm === '' || confirm == null) {
      return null;
    }

    return newPassword === confirm ? null : { passwordMismatch: true };
  }

  onSubmitPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.isPasswordSubmitting = true;
    clearServerErrorsFromForm(this.passwordForm);

    const { currentPassword, newPassword, newPasswordConfirmation } = this.passwordForm.getRawValue();

    this.userAuth.changePassword({ currentPassword, newPassword, newPasswordConfirmation }).subscribe({
      next: (res) => {
        this.isPasswordSubmitting = false;
        this.passwordForm.reset();
        this.showCurrentPassword = false;
        this.showNewPassword = false;
        this.showNewPasswordConfirmation = false;

        const baseMsg = res.message?.trim() || 'La contraseña fue actualizada correctamente.';
        this.toastr.success(
          `${baseMsg} Por seguridad se cerrará la sesión en unos segundos; deberá volver a iniciar sesión.`,
          'Éxito',
          { timeOut: 5000 },
        );

        this.isPasswordChangeLogoutPending = true;
        this.clearPasswordChangeLogoutTimer();
        this.passwordChangeLogoutTimer = setTimeout(() => {
          this.passwordChangeLogoutTimer = undefined;
          void this.router.navigate(['/cliente/logout/1']);
        }, PerfilComponent.passwordChangeLogoutDelayMs);
      },
      error: (err: HttpErrorResponse) => {
        this.isPasswordSubmitting = false;

        this.httpErrorHandler.show(err);

        const validationErrors = this.httpErrorHandler.getValidationErrors(err);

        if (validationErrors) {
          applyValidationErrorsToForm(this.passwordForm, validationErrors);
        }
      },
    });
  }

  get serviceStatus(): string {
    return this.institutionalService?.status || 'ACTIVO';
  }

  get hasContractedPlan(): boolean {
    const plan = this.institutionalService?.contractedPlan;
    return !!plan && (plan.downloadMbps != null || plan.uploadMbps != null);
  }

  get serviceLocalityLabel(): string | null {
    const locality = this.institutionalService?.locality;
    if (!locality) {
      return null;
    }

    const parts = [locality.district, locality.province, locality.department].filter((part) => !!part);

    return parts.length ? parts.join(' - ') : null;
  }

  ngOnInit() {
    this.getUser(this.identity.sub);
    document.querySelector('body')?.classList.remove('modal-open');
  }

  setForm(user: User) {
    this.form.setValue({
      cellphone1: user.userDetails?.cellPhone,
      cellphone2: user.userDetails?.cellPhone2,
      phone: user.userDetails?.phone,
      email: user.userDetails?.email,
    });
  }
  isEnabled(data: ReciboCliente[], receiptId: number): boolean {
    let index = null;
    let enabled = true;
    for (let i = 0; i < data.length; i++) {
      if (data[i].id == receiptId) index = i;

      if (index != null && i > index && data[i].estado == 'VENCIDO') {
        enabled = false;
        break;
      }
    }

    return enabled;
  }

  getUser(id: number) {
    this.isLoadingProfile = true;
    this.institutionalService = null;

    const services$ = this.isSchool
      ? this._api.customer.getInstitutionalServices().pipe(
          catchError((err) => {
            this._log.error(err);
            return of([] as InstitutionalServiceContext[]);
          }),
        )
      : of([] as InstitutionalServiceContext[]);

    forkJoin({
      user: this._api.customer.getUser(id),
      services: services$,
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ({ user, services }) => {
          this.user = user;
          this.setForm(this.user);
          this.identity.celular = this.user.userDetails?.cellPhone;
          localStorage.setItem('identity', JSON.stringify(this.identity));
          this.institutionalService = this.isSchool && Array.isArray(services) ? services[0] ?? null : null;

          this.route.queryParams.pipe(take(1), takeUntil(this.ngUnsubscribe)).subscribe((queryParams) => {
            if (queryParams['email']) {
              this.editContactInformation();
            }
          });
          this.isLoadingProfile = false;
        },
        error: (err) => {
          this._log.error(err);
          this.isLoadingProfile = false;
        },
      });
  }

  getPerson(userDetail: UserDetails): Person {
    return {
      id: userDetail.id,
      dni: userDetail.dni,
      ruc: userDetail.ruc,
      lastName: userDetail.lastName || '',
      firstName: userDetail.firstName || '',
      email: userDetail.email,
      cellPhone: userDetail.cellPhone,
      cellPhone2: userDetail.cellPhone2,
      phone: userDetail.phone,
    };
  }

  getCompany(userDetail: UserDetails): Company {
    return {
      id: userDetail.id,
      ruc: userDetail.ruc || '',
      name: userDetail.name || '',
      businessName: userDetail.businessName || '',
      email: userDetail.email,
      cellPhone: userDetail.cellPhone,
      cellPhone2: userDetail.cellPhone2,
      phone: userDetail.phone,
    };
  }

  onSubmit() {
    const data = this.form.value;
    if (this.form.valid) {
      if (this.user.userDetails) {
        this.user.userDetails.cellPhone = data.cellphone1;
        this.user.userDetails.cellPhone2 = data.cellphone2;
        this.user.userDetails.phone = data.phone;
        this.user.userDetails.email = data.email;
      }

      const body: User = {
        email: data.email,
        userType: this.user.userType,
        userDetails: this.user.userDetails,
      };

      this.isDelay = true;

      this._api.customer.updateUser(body, this.user.id || 0).subscribe({
        next: () => {
          this.isDelay = false;
          this.editContact = false;
          this.editContactIcon = 'icon-edit';
          if (this.user.userDetails?.email) this.identity.email = this.user.userDetails.email;

          localStorage.setItem('identity', JSON.stringify(this.identity)); //actualizar datos del usuario en la sesión

          this.data_saved_successfully = true;
          this.registration_successful_message = 'Se registraron los cambios satisfactoriamente.';
          this.clearQueryParam();
        },
        error: (error) => {
          this.isDelay = false;
          this.registration_error_message = 'Ocurrió un error en el registro.';
          this.data_saved_successfully = false;
          this._log.error(error);
        },
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  clearQueryParam() {
    const currentUrl = this.location.path();
    const urlWithoutQueryParams = currentUrl.split('?')[0];
    this.location.replaceState(urlWithoutQueryParams);
  }

  editContactInformation() {
    this.editContact = true;
    this.editContactIcon = 'icon-x';
    this.form.markAllAsTouched();
  }

  resetValues() {
    this.registration_successful_message = undefined;
    this.registration_error_message = undefined;
    this.data_saved_successfully = false;
  }

  ngOnDestroy(): void {
    this.clearPasswordChangeLogoutTimer();
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  private clearPasswordChangeLogoutTimer(): void {
    if (this.passwordChangeLogoutTimer != null) {
      clearTimeout(this.passwordChangeLogoutTimer);
      this.passwordChangeLogoutTimer = undefined;
    }
  }
}
